'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
// The app's own gated downloadable-app modules (Data Downloader + the future binary apps). Merged into the
// Modules-tab catalog so they can be enabled per customer even though the platform Module catalog has no row
// for them (creating a Module is a restricted op; the CustomerModuleJoin gate is keyed by ModuleHash, no FK).
const libDataDownloaderProvider = require('./DataDownloader-Provider.js');

/**
 * ManageCustomersProvider — data + logic for the #/ManageCustomers screen (platform super-users only).
 *
 * Loads the customer list and renders a filterable table into #MC-Body; each row's "Manage Configuration" button
 * kicks off impersonation of that customer's configuration admin through the SINGLE app client method
 * PlatformConfiguration-Application-Web.impersonateCustomerAdmin(IDCustomer) (the forward-compatible seam — the
 * future level-4 cross-customer flow reuses it). The level gate is the app's _isSuperUser().
 *
 * Server-managed tenancy: the client never sends IDCustomer in a body; the customer list comes back scoped by the
 * super-user session, and the impersonate call takes IDCustomer only as a route parameter.
 */
class ManageCustomersProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this.state = { Loaded: false, Loading: false, Error: '', Customers: [], Filter: '', Mode: 'customers', TeamSearch: '', TeamResults: [], TeamSearching: false, TeamError: '',
			// Customers tab: which customer row is expanded to pick a configuration-admin account to impersonate, the
			// per-customer account cache, and the load status. Picking one signs in as that specific account (no modal).
			ExpandedCustomerID: 0, AccountsByCustomer: {}, AccountsLoadingID: 0, AccountsError: '',
			// Modules tab: enable/disable Modules (CustomerModuleJoin) for a selected customer.
			ModulesCatalog: [], ModulesCustomerID: 0, ModulesJoins: [], ModulesLoading: false, ModulesError: '', ModulesFilter: '', ModulesBusyHash: '' };
		this._teamSearchTimer = null;
		this._teamSearchToken = 0;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get app() { return this.pict.PictApplication; }
	/** The shared pict-section-modal (confirm/toast); guarded — it may be absent. */
	get modal() { return this.pict.views['Pict-Section-Modal']; }

	_toast(pMessage, pType)
	{
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); }
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody)));
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody)));
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody)));
	}

	_deleteEntity(pEntity, pID)
	{
		return new Promise((resolve, reject) => this.restClient.deleteEntity(pEntity, pID, (pError, pBody) => pError ? reject(pError) : resolve(pBody)));
	}

	/** True when this super-user also holds the "Manage Customer Team" entitlement (delegates to the app's one gate). */
	_canManageTeam() { return !!(this.app && (typeof this.app._canManageCustomerTeam === 'function') && this.app._canManageCustomerTeam()); }

	/** UI gate — a level-5 super-admin OR a customer-1 support user with the Customer-Impersonate capability. The server
	 *  (Authentication.authorizeImpersonation) stays authoritative for the customer list + impersonation. */
	isAuthorized() { return !!(this.app && (typeof this.app.canImpersonate === 'function') && this.app.canImpersonate()); }
	/** The Modules + Customer Team tabs stay level-5 super-admin only (support staff impersonate, but don't license modules). */
	_isSuperUser() { return !!(this.app && (typeof this.app._isSuperUser === 'function') && this.app._isSuperUser()); }

	async loadAsync()
	{
		if (!this.isAuthorized()) { return; }
		this.state.Loading = true;
		this.state.Error = '';
		this.render();
		try
		{
			// The server scopes the list to what this super-user session may see (no client-side customer filter).
			const tmpRows = await this._getJSON('Customers/0/2000');
			this.state.Customers = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pCustomer) => ({ IDCustomer: pCustomer.IDCustomer, Name: pCustomer.Name || `Customer #${pCustomer.IDCustomer}`, Code: pCustomer.Code || '', Enabled: pCustomer.Enabled }))
				.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
			this.state.Loaded = true;
		}
		catch (pError)
		{
			this.state.Error = `Could not load customers: ${(pError && pError.message) ? pError.message : pError}`;
		}
		this.state.Loading = false;
		this.render();
	}

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#MC-Body').length === 0) { return; }
		if (!this.isAuthorized()) { tmpCA.assignContent('#MC-Body', this._guardHTML()); return; }
		tmpCA.assignContent('#MC-Body', this._tabsHTML() + `<div id="MC-Mode">${this._modeHTML()}</div>`);
		// assignContent is synchronous (innerHTML), so the picker mount point exists now — mount it in place.
		this._mountModePickers();
	}

	/** Tabs. Customers = anyone who reaches the screen (impersonators). Modules + Customer Team stay super-user only. */
	_tabsHTML()
	{
		const fTab = (pMode, pLabel) => `<button type="button" class="mc-tab${(this.state.Mode === pMode) ? ' is-active' : ''}" onclick="_Pict.providers.ManageCustomers.navigateMode('${pMode}')">${pLabel}</button>`;
		const tmpTabs = [ fTab('customers', 'Customers') ];
		if (this._isSuperUser()) { tmpTabs.push(fTab('modules', 'Modules')); }
		if (this._canManageTeam()) { tmpTabs.push(fTab('team', 'Customer Team')); }
		if (tmpTabs.length < 2) { return ''; }   // only Customers — no tab strip needed
		return `<div class="mc-tabs">${tmpTabs.join('')}</div>`;
	}

	_modeHTML()
	{
		// Modules + Customer Team are super-user-only surfaces; a support user landing on their route falls back to Customers.
		if ((this.state.Mode === 'team') && this._canManageTeam()) { return this._teamHTML(); }
		if ((this.state.Mode === 'modules') && this._isSuperUser()) { return this._modulesHTML(); }
		return this._bodyHTML();
	}

	/** Switch tabs. 'team' requires the entitlement; falls back to 'customers' otherwise. */
	/** Switch mode via a stable route (#/ManageCustomers/<Mode>) so the active tab lives in the URL. */
	navigateMode(pMode)
	{
		if ((typeof window !== 'undefined') && window.location) { window.location.hash = `#/ManageCustomers/${ pMode }`; }
		else { this.setMode(pMode); }
	}

	setMode(pMode)
	{
		const tmpAllowed = { customers: true, modules: true, team: this._canManageTeam() };
		this.state.Mode = tmpAllowed[pMode] ? pMode : 'customers';
		this.render();
	}

	/** Filter in place — only the list re-renders, so the filter input keeps focus. */
	onFilter(pValue)
	{
		this.state.Filter = pValue;
		this._renderList();
	}

	/** Repaint just the customer list (keeps the filter input alive + preserves the list's scroll position — no modal,
	 *  no full re-render). Used by the filter and by expanding/collapsing a customer's accounts. */
	_renderList()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpEl = tmpCA.getElement('#MC-List');
		if (!tmpEl || !tmpEl.length) { return; }
		const tmpTop = tmpEl[0].scrollTop;
		tmpCA.assignContent('#MC-List', this._listHTML());
		const tmpEl2 = tmpCA.getElement('#MC-List');
		if (tmpEl2 && tmpEl2.length) { tmpEl2[0].scrollTop = tmpTop; }
	}

	/** Expand (or collapse) a customer row to show its configuration-admin accounts. First expand loads them. No modal. */
	toggleAccounts(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.ExpandedCustomerID = 0; this._renderList(); return; }
		this.state.ExpandedCustomerID = tmpID;
		this.state.AccountsError = '';
		if (!this.state.AccountsByCustomer[tmpID]) { this._loadAccountsAsync(tmpID); }
		this._renderList();
	}

	/**
	 * Load a customer's impersonatable accounts — the users who hold a PermissionLevel-'CustomerAdmin' role in that
	 * customer (the config-app write gate). Mirrors the server's resolveCustomerAdminUser query, but lists ALL of them.
	 * Cross-customer reads ride the super-user session (same pattern as SuperUserGrant / Entitlements).
	 */
	async _loadAccountsAsync(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		this.state.AccountsLoadingID = tmpID;
		this.state.AccountsError = '';
		this._renderList();
		try
		{
			const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~PermissionLevel~EQ~CustomerAdmin~FBV~Deleted~EQ~0/0/200`);
			const tmpRoleIDs = (Array.isArray(tmpRoles) ? tmpRoles : []).map((pRole) => pRole.IDRole).filter(Boolean);
			let tmpAccounts = [];
			if (tmpRoleIDs.length)
			{
				const tmpJoins = await this._getJSON(`UserRoleJoins/FilteredTo/FBL~IDRole~INN~${tmpRoleIDs.join(',')}/0/1000`);
				const tmpUserIDs = Array.from(new Set((Array.isArray(tmpJoins) ? tmpJoins : []).map((pJoin) => pJoin.IDUser).filter(Boolean)));
				if (tmpUserIDs.length)
				{
					// Only live, enabled users actually IN the target customer qualify (mirrors the server's gate).
					const tmpUsers = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpUserIDs.join(',')}~FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0~FBV~Disabled~EQ~0/0/1000`);
					tmpAccounts = (Array.isArray(tmpUsers) ? tmpUsers : [])
						.map((pUser) => ({ IDUser: pUser.IDUser, Name: libUserSearch.userLabel(pUser), LoginID: pUser.LoginID || '', Email: pUser.Email || '' }))
						.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
				}
			}
			if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.AccountsByCustomer[tmpID] = tmpAccounts; }
		}
		catch (pError)
		{
			if (Number(this.state.ExpandedCustomerID) === tmpID) { this.state.AccountsError = `Could not load accounts: ${(pError && pError.message) ? pError.message : pError}`; }
		}
		this.state.AccountsLoadingID = 0;
		this._renderList();
	}

	/** Sign in as a specific account (no modal) via the app's per-user impersonation seam. */
	impersonateAccount(pIDUser)
	{
		if (this.app && (typeof this.app.impersonateUser === 'function')) { this.app.impersonateUser(Number(pIDUser)); }
	}

	_guardHTML()
	{
		return `<div class="mc-guard"><div class="mc-guard-title">Platform super-users only</div><p>This tool is available to level-5 platform administrators.</p></div>`;
	}

	_bodyHTML()
	{
		if (this.state.Loading && !this.state.Loaded) { return '<div class="mc-note"><i class="fas fa-spinner fa-spin"></i> Loading customers…</div>'; }
		if (this.state.Error) { return `<div class="mc-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.Error)}</div>`; }
		return `<input id="MC-Filter" class="mc-filter" type="text" autocomplete="off" placeholder="Filter customers by name or code…" oninput="_Pict.providers.ManageCustomers.onFilter(this.value)" />`
			+ `<div id="MC-List" class="mc-list">${this._listHTML()}</div>`;
	}

	_listHTML()
	{
		const tmpTerm = String(this.state.Filter || '').trim().toLowerCase();
		const tmpRows = this.state.Customers.filter((pCustomer) =>
			!tmpTerm || (String(pCustomer.Name).toLowerCase().indexOf(tmpTerm) > -1) || (String(pCustomer.Code).toLowerCase().indexOf(tmpTerm) > -1));
		if (!tmpRows.length) { return `<div class="mc-note">${tmpTerm ? 'No customers match your filter.' : 'No customers available.'}</div>`; }
		const tmpHeader = '<div class="mc-head-row"><span class="mc-col-name">Customer</span><span class="mc-col-code">Code</span><span class="mc-col-action"></span></div>';
		return tmpHeader + tmpRows.map((pCustomer) =>
		{
			const tmpID = Number(pCustomer.IDCustomer);
			const tmpExpanded = (Number(this.state.ExpandedCustomerID) === tmpID);
			const tmpDisabled = (Number(pCustomer.Enabled) === 0) ? ' <span class="mc-disabled">(disabled)</span>' : '';
			return `<div class="mc-item${tmpExpanded ? ' is-expanded' : ''}">`
				+ `<div class="mc-row">`
				+ `<span class="mc-col-name">${this._esc(pCustomer.Name)}${tmpDisabled}</span>`
				+ `<span class="mc-col-code">${this._esc(pCustomer.Code)}</span>`
				+ `<span class="mc-col-action"><button type="button" class="mc-btn${tmpExpanded ? ' is-open' : ''}" onclick="_Pict.providers.ManageCustomers.toggleAccounts(${tmpID})"><i class="fas fa-user-gear"></i> Manage Configuration <i class="fas fa-chevron-${tmpExpanded ? 'up' : 'down'} mc-btn-caret"></i></button></span>`
				+ `</div>`
				+ (tmpExpanded ? `<div class="mc-accounts">${this._accountsHTML(tmpID)}</div>` : '')
				+ `</div>`;
		}).join('');
	}

	/** The impersonatable accounts for one customer: loading / error / empty / a list of "Sign in as" account buttons. */
	_accountsHTML(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (this.state.AccountsError) { return `<div class="mc-accounts-note mc-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.AccountsError)}</div>`; }
		const tmpAccounts = this.state.AccountsByCustomer[tmpID];
		if (!tmpAccounts) { return `<div class="mc-accounts-note mc-note"><i class="fas fa-spinner fa-spin"></i> Loading accounts…</div>`; }
		if (!tmpAccounts.length)
		{
			return `<div class="mc-accounts-note mc-note">No configuration admin for this customer yet — <a href="#/GrantAccess">grant one in Grant Access</a> first.</div>`;
		}
		const tmpHead = '<div class="mc-accounts-head">Sign in as a configuration admin</div>';
		return tmpHead + tmpAccounts.map((pAccount) =>
			`<button type="button" class="mc-account" onclick="_Pict.providers.ManageCustomers.impersonateAccount(${Number(pAccount.IDUser)})">`
			+ `<span class="mc-account-avatar"><i class="fas fa-user"></i></span>`
			+ `<span class="mc-account-id"><span class="mc-account-name">${this._esc(pAccount.Name)}</span><span class="mc-account-login">${this._esc(pAccount.LoginID || pAccount.Email || `#${pAccount.IDUser}`)}</span></span>`
			+ `<span class="mc-account-go">Sign in <i class="fas fa-arrow-right-to-bracket"></i></span>`
			+ `</button>`).join('');
	}

	// --- Modules: enable/disable a customer's licensed Modules (CustomerModuleJoin) ---------------------
	//
	// A super-user's Meadow reads/writes are not customer-scoped, so this can read/write any customer's rows by
	// passing an explicit IDCustomer (the same cross-customer pattern SuperUserGrant uses for Role). Enablement is
	// a CustomerModuleJoin row {IDCustomer, ModuleHash, StartDate, EndDate}; disable is a physical delete (the
	// entity has no soft-delete column). The catalog of modules is the global Module entity.

	_moduleName(pModuleHash)
	{
		const tmpModule = (this.state.ModulesCatalog || []).find((pModule) => pModule.ModuleHash === pModuleHash);
		return tmpModule ? tmpModule.Name : pModuleHash;
	}

	/** Is a module currently enabled for the selected customer (a join row whose [Start,End] window holds now)? */
	_isModuleEnabled(pModuleHash)
	{
		const tmpNow = (new Date()).toISOString();
		return (this.state.ModulesJoins || []).some((pJoin) => (pJoin.ModuleHash === pModuleHash)
			&& (!pJoin.StartDate || String(pJoin.StartDate) <= tmpNow) && (!pJoin.EndDate || String(pJoin.EndDate) >= tmpNow));
	}

	_modulesHTML()
	{
		// The customer selector is a searchable pict-section-picker (mounted after paint by _mountModePickers), NOT a
		// native <select> — the roster runs to thousands of customers, so it must be type-to-filter.
		return `<p class="mc-team-help">Enable or disable licensed <strong>Modules</strong> for a customer. A customer only sees a gated feature (e.g. the Data Downloader) once its module is enabled here. Changes take effect the next time that customer loads the app.</p>`
			+ `<div class="mc-module-picker"><label class="mc-module-picklabel">Customer</label>`
			+ `<div id="MC-Module-Cust-Pick" class="mc-module-custpick"></div></div>`
			+ `<div id="MC-ModuleResults" class="mc-module-results">${this._moduleResultsHTML()}</div>`;
	}

	/** A "Name (CODE)" label for a customer roster row. */
	_customerLabel(pCustomer)
	{
		if (!pCustomer) { return ''; }
		return `${pCustomer.Name || `Customer #${pCustomer.IDCustomer}`}${pCustomer.Code ? ` (${pCustomer.Code})` : ''}`;
	}

	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }

	/** DataProvider over the in-memory customer roster (already loaded whole). Returns {results:[{Value,Text}], hasMore}. */
	_moduleCustomerDataProvider()
	{
		return (pTerm) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
			const tmpRows = (this.state.Customers || [])
				.filter((pCustomer) => !tmpTerm
					|| (String(pCustomer.Name || '').toLowerCase().indexOf(tmpTerm) >= 0)
					|| (String(pCustomer.Code || '').toLowerCase().indexOf(tmpTerm) >= 0))
				.map((pCustomer) => ({ Value: Number(pCustomer.IDCustomer), Text: this._customerLabel(pCustomer) }));
			return Promise.resolve({ results: tmpRows.slice(0, 60), hasMore: tmpRows.length > 60 });
		};
	}

	_moduleCustomerResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			const tmpCustomer = (this.state.Customers || []).find((pCustomer) => Number(pCustomer.IDCustomer) === tmpID);
			return Promise.resolve(tmpCustomer ? { Value: tmpID, Text: this._customerLabel(tmpCustomer) } : null);
		};
	}

	/** Mount the searchable pickers for whatever mode just painted (currently just the Modules-tab customer picker). */
	_mountModePickers()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpPicker = this.pickerProvider;
		if (!tmpCA || !tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if ((this.state.Mode !== 'modules') || !this._isSuperUser()) { return; }
		if (tmpCA.getElement('#MC-Module-Cust-Pick').length === 0) { return; }
		if (!this.pict.AppData.ManageCustomerPickers) { this.pict.AppData.ManageCustomerPickers = {}; }
		const tmpInstance = tmpPicker.createPicker('ManageCustomers-ModuleCustomer',
			{
				DestinationAddress: '#MC-Module-Cust-Pick',
				ValueAddress: 'AppData.ManageCustomerPickers.ModuleCustomer',
				Searchable: true,
				AllowClear: true,
				ClearLabel: '— choose a customer —',
				Placeholder: 'Search customers by name or code…',
				DataProvider: this._moduleCustomerDataProvider(),
				ResolveValue: this._moduleCustomerResolveValue(),
				OnChange: (pValue) => this.selectModuleCustomer(Number(pValue) || 0),
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
		if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(Number(this.state.ModulesCustomerID) || ''); }
	}

	_moduleResultsHTML()
	{
		if (!this.state.ModulesCustomerID) { return '<div class="mc-note">Choose a customer to manage its modules.</div>'; }
		if (this.state.ModulesLoading) { return '<div class="mc-note"><i class="fas fa-spinner fa-spin"></i> Loading modules…</div>'; }
		if (this.state.ModulesError) { return `<div class="mc-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.ModulesError)}</div>`; }
		return `<input id="MC-ModuleFilter" class="mc-filter" type="text" autocomplete="off" placeholder="Filter modules by name…" value="${this._esc(this.state.ModulesFilter)}" oninput="_Pict.providers.ManageCustomers.onModuleFilter(this.value)" />`
			+ `<div id="MC-ModuleList" class="mc-list">${this._moduleListHTML()}</div>`;
	}

	_moduleListHTML()
	{
		const tmpTerm = String(this.state.ModulesFilter || '').trim().toLowerCase();
		const tmpRows = this.state.ModulesCatalog.filter((pModule) =>
			!tmpTerm || (String(pModule.Name).toLowerCase().indexOf(tmpTerm) > -1) || (String(pModule.ModuleHash).toLowerCase().indexOf(tmpTerm) > -1));
		if (!tmpRows.length) { return `<div class="mc-note">${tmpTerm ? 'No modules match your filter.' : 'No modules in the catalog.'}</div>`; }
		return tmpRows.map((pModule) =>
		{
			const tmpOn = this._isModuleEnabled(pModule.ModuleHash);
			const tmpAppTag = pModule.App ? ' <span class="mc-app-tag">Desktop app</span>' : '';
			const tmpBadge = tmpOn ? ' <span class="mc-team-badge">Enabled</span>' : '';
			const tmpBusy = (this.state.ModulesBusyHash === pModule.ModuleHash);
			let tmpButton;
			if (tmpBusy) { tmpButton = '<button type="button" class="mc-btn" disabled><i class="fas fa-spinner fa-spin"></i></button>'; }
			else if (tmpOn) { tmpButton = `<button type="button" class="mc-btn mc-btn-remove" onclick="_Pict.providers.ManageCustomers.disableModule('${this._esc(pModule.ModuleHash)}')"><i class="fas fa-toggle-on"></i> Disable</button>`; }
			else { tmpButton = `<button type="button" class="mc-btn" onclick="_Pict.providers.ManageCustomers.enableModule('${this._esc(pModule.ModuleHash)}')"><i class="fas fa-toggle-off"></i> Enable</button>`; }
			return `<div class="mc-row">`
				+ `<span class="mc-col-name">${this._esc(pModule.Name || pModule.ModuleHash)}${tmpAppTag}${tmpBadge}</span>`
				+ `<span class="mc-col-code">${this._esc(pModule.ModuleHash)}</span>`
				+ `<span class="mc-col-action">${tmpButton}</span>`
				+ `</div>`;
		}).join('');
	}

	onModuleFilter(pValue)
	{
		this.state.ModulesFilter = pValue;
		this._renderModuleList();
	}

	selectModuleCustomer(pIDCustomer)
	{
		this.state.ModulesCustomerID = Number(pIDCustomer) || 0;
		this.state.ModulesFilter = '';
		if (this.state.ModulesCustomerID) { this._loadModuleDataAsync(); }
		else { this._renderModuleResults(); }
	}

	async _loadModuleDataAsync()
	{
		this.state.ModulesLoading = true;
		this.state.ModulesError = '';
		this._renderModuleResults();
		try
		{
			if (!this.state.ModulesCatalog.length)
			{
				const tmpCatalog = await this._getJSON('Modules/0/500');
				const tmpRows = (Array.isArray(tmpCatalog) ? tmpCatalog : [])
					.map((pModule) => ({ ModuleHash: pModule.ModuleHash, Name: pModule.Name || pModule.ModuleHash, Ordinal: Number(pModule.Ordinal) || 0, App: false }))
					.filter((pModule) => !!pModule.ModuleHash);
				// Merge the app's gated downloadable-app modules that aren't already in the platform catalog, so they
				// can be enabled per customer without a catalog row (the join gate is keyed by ModuleHash).
				const tmpHave = {};
				tmpRows.forEach((pModule) => { tmpHave[pModule.ModuleHash] = true; });
				(((libDataDownloaderProvider && libDataDownloaderProvider.DOWNLOAD_MODULES) || [])).forEach((pApp) =>
				{
					if (pApp && pApp.ModuleHash && !tmpHave[pApp.ModuleHash]) { tmpRows.push({ ModuleHash: pApp.ModuleHash, Name: pApp.Name || pApp.ModuleHash, Ordinal: 900, App: true }); }
				});
				this.state.ModulesCatalog = tmpRows.sort((pA, pB) => (pA.Ordinal - pB.Ordinal) || String(pA.Name).localeCompare(String(pB.Name)));
			}
			await this._refreshJoins();
			this.state.ModulesLoading = false;
			this._renderModuleResults();
		}
		catch (pError)
		{
			this.state.ModulesLoading = false;
			this.state.ModulesError = `Could not load modules: ${(pError && pError.message) ? pError.message : pError}`;
			this._renderModuleResults();
		}
	}

	async _refreshJoins()
	{
		const tmpJoins = await this._getJSON(`CustomerModuleJoins/FilteredTo/FBV~IDCustomer~EQ~${this.state.ModulesCustomerID}/0/500`);
		this.state.ModulesJoins = Array.isArray(tmpJoins) ? tmpJoins : [];
	}

	_renderModuleResults()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#MC-ModuleResults').length) { tmpCA.assignContent('#MC-ModuleResults', this._moduleResultsHTML()); }
	}

	_renderModuleList()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#MC-ModuleList').length) { tmpCA.assignContent('#MC-ModuleList', this._moduleListHTML()); }
	}

	async enableModule(pModuleHash)
	{
		if (!this.state.ModulesCustomerID || this.state.ModulesBusyHash) { return; }
		this.state.ModulesBusyHash = pModuleHash;
		this._renderModuleList();
		try
		{
			// Cross-customer create — explicit IDCustomer (a super-user is authorized server-side; see SuperUserGrant).
			const tmpSaved = await this._postJSON('CustomerModuleJoin',
				{ IDCustomer: Number(this.state.ModulesCustomerID), ModuleHash: pModuleHash, IDProgram: 0, StartDate: '2000-01-01T00:00:00.000Z', EndDate: '2050-01-01T00:00:00.000Z' });
			if (tmpSaved && tmpSaved.ErrorCode) { throw new Error('the server rejected the change'); }
			await this._refreshJoins();
			// The server may silently no-op an unauthorized write (empty body, no ErrorCode) — confirm it took.
			if (!this._isModuleEnabled(pModuleHash)) { throw new Error('the change did not take effect (you may not have permission).'); }
			this._toast(`Enabled ${this._moduleName(pModuleHash)} for this customer.`, 'success');
		}
		catch (pError)
		{
			this._toast(`Could not enable the module: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
		this.state.ModulesBusyHash = '';
		this._renderModuleList();
	}

	disableModule(pModuleHash)
	{
		if (!this.state.ModulesCustomerID || this.state.ModulesBusyHash) { return; }
		const tmpJoins = (this.state.ModulesJoins || []).filter((pJoin) => pJoin.ModuleHash === pModuleHash);
		if (!tmpJoins.length) { return; }
		const fGo = async () =>
		{
			this.state.ModulesBusyHash = pModuleHash;
			this._renderModuleList();
			try
			{
				for (const pJoin of tmpJoins) { await this._deleteEntity('CustomerModuleJoin', Number(pJoin.IDCustomerModuleJoin)); }
				await this._refreshJoins();
				if (this._isModuleEnabled(pModuleHash)) { throw new Error('the change did not take effect (you may not have permission).'); }
				this._toast(`Disabled ${this._moduleName(pModuleHash)} for this customer.`, 'success');
			}
			catch (pError)
			{
				this._toast(`Could not disable the module: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			}
			this.state.ModulesBusyHash = '';
			this._renderModuleList();
		};
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(`Disable ${this._moduleName(pModuleHash)} for this customer? This removes their access to the feature.`,
				{ title: 'Disable module', confirmLabel: 'Disable', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fGo(); } });
		}
		else { fGo(); }
	}

	// --- Customer Team: mark internal users as Headlight platform admins -------------------------------

	/** Whether a user is currently a Headlight platform admin (the flat "customer team" marker). */
	_isPlatformAdmin(pUser)
	{
		return (pUser.Classification === 'Headlight_Platform_Admin') && ((Number(pUser.PersistentPassword) === 1) || (pUser.PersistentPassword === true));
	}


	_teamHTML()
	{
		return `<p class="mc-team-help">Search any user, across every customer, and mark them as a <strong>Headlight platform admin</strong> — the internal team who may impersonate a customer&#8217;s configuration admin. This sets the user&#8217;s <code>Classification</code> and <code>PersistentPassword</code>.</p>`
			+ `<input id="MC-TeamSearch" class="mc-filter" type="text" autocomplete="off" placeholder="Search users by name, login ID or email…" value="${this._esc(this.state.TeamSearch)}" oninput="_Pict.providers.ManageCustomers.onTeamSearch(this.value)" />`
			+ `<div id="MC-TeamResults" class="mc-list">${this._teamResultsHTML()}</div>`;
	}

	_teamResultsHTML()
	{
		if (this.state.TeamError) { return `<div class="mc-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.TeamError)}</div>`; }
		if (this.state.TeamSearching) { return '<div class="mc-note"><i class="fas fa-spinner fa-spin"></i> Searching…</div>'; }
		const tmpTerm = String(this.state.TeamSearch || '').trim();
		if (tmpTerm.length < 2) { return '<div class="mc-note">Type at least 2 characters to search users.</div>'; }
		const tmpRows = this.state.TeamResults;
		if (!tmpRows.length) { return '<div class="mc-note">No users match your search.</div>'; }
		return tmpRows.map((pUser) =>
		{
			const tmpIsAdmin = this._isPlatformAdmin(pUser);
			const tmpBadge = tmpIsAdmin ? ' <span class="mc-team-badge">Platform admin</span>' : '';
			const tmpButton = tmpIsAdmin
				? `<button type="button" class="mc-btn mc-btn-remove" onclick="_Pict.providers.ManageCustomers.togglePlatformAdmin(${Number(pUser.IDUser)}, false)"><i class="fas fa-user-minus"></i> Remove from team</button>`
				: `<button type="button" class="mc-btn" onclick="_Pict.providers.ManageCustomers.togglePlatformAdmin(${Number(pUser.IDUser)}, true)"><i class="fas fa-user-plus"></i> Add to team</button>`;
			return `<div class="mc-row">`
				+ `<span class="mc-col-name">${this._esc(libUserSearch.userLabel(pUser))}${tmpBadge}</span>`
				+ `<span class="mc-col-code">${this._esc(pUser.Email || '')}</span>`
				+ `<span class="mc-col-action">${tmpButton}</span>`
				+ `</div>`;
		}).join('');
	}

	/** Debounced cross-customer user search; only the results re-render, so the search input keeps focus. */
	onTeamSearch(pValue)
	{
		this.state.TeamSearch = pValue;
		if (this._teamSearchTimer) { clearTimeout(this._teamSearchTimer); }
		this._teamSearchTimer = setTimeout(() => { this._teamSearchTimer = null; this._teamSearchAsync(); }, 250);
	}

	async _teamSearchAsync()
	{
		const tmpTerm = String(this.state.TeamSearch || '').trim();
		const tmpToken = ++this._teamSearchToken;
		if (tmpTerm.length < 2)
		{
			this.state.TeamResults = [];
			this.state.TeamSearching = false;
			this.state.TeamError = '';
			this._renderTeamResults();
			return;
		}
		this.state.TeamSearching = true;
		this.state.TeamError = '';
		this._renderTeamResults();
		try
		{
			// Cross-customer (no customer clause) — the super-user session may search everyone (like Grant Access).
			const tmpUsers = await libUserSearch.searchUsersAsync((pURL) => this._getJSON(pURL), tmpTerm, { PageSize: 50 });
			if (tmpToken !== this._teamSearchToken)
			{
				return;
			}
			this.state.TeamResults = tmpUsers;
		}
		catch (pError)
		{
			if (tmpToken !== this._teamSearchToken)
			{
				return;
			}
			this.state.TeamResults = [];
			this.state.TeamError = 'Could not search users. Try again.';
		}
		this.state.TeamSearching = false;
		this._renderTeamResults();
	}

	_renderTeamResults()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#MC-TeamResults').length) { tmpCA.assignContent('#MC-TeamResults', this._teamResultsHTML()); }
	}

	/** Add/remove a user from the platform-admin team by setting Classification + PersistentPassword (no IDCustomer sent). */
	togglePlatformAdmin(pIDUser, pOn)
	{
		const tmpUser = (this.state.TeamResults || []).find((pUser) => Number(pUser.IDUser) === Number(pIDUser));
		if (!tmpUser) { return; }
		const tmpPayload = { IDUser: Number(pIDUser), Classification: pOn ? 'Headlight_Platform_Admin' : '', PersistentPassword: pOn ? 1 : 0 };
		this._putJSON('User', tmpPayload).then((pSaved) =>
		{
			if (!pSaved || pSaved.ErrorCode)
			{
				this._toast('Could not update the user (you may not have permission to edit this customer’s users).', 'error');
				return;
			}
			tmpUser.Classification = tmpPayload.Classification;
			tmpUser.PersistentPassword = tmpPayload.PersistentPassword;
			this._toast(pOn ? `Added ${libUserSearch.userLabel(tmpUser)} to the platform-admin team.` : `Removed ${libUserSearch.userLabel(tmpUser)} from the platform-admin team.`, 'success');
			this._renderTeamResults();
		}).catch((pError) =>
		{
			this._toast(`Could not update the user: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		});
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
}

ManageCustomersProvider.default_configuration = { ProviderIdentifier: 'ManageCustomers' };

module.exports = ManageCustomersProvider;
module.exports.default_configuration = ManageCustomersProvider.default_configuration;
